import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Field, Select, TextInput } from '@/components/Field'
import { Kicker } from '@/components/Kicker'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { PACK_COLORS, PACK_LABELS, type PackKey } from '@/design-system/tokens'

/**
 * FamilyBootstrapScreen — calls the `families` Edge Function to create:
 *   - family row (with active_packs)
 *   - owner adult Person (Dan)
 *   - optional baby/unborn Person
 *   - parent_of relationship
 *   - family.created + person.created events
 * Then refreshes the app store to enter the active flow.
 */
export function FamilyBootstrapScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)
  const refresh = useAppStore((s) => s.refreshSession)

  const [ownerName, setOwnerName] = useState('')
  const [familyName, setFamilyName] = useState('')
  const [childType, setChildType] = useState<'baby' | 'unborn' | 'none'>('baby')
  const [childName, setChildName] = useState('')
  const [childDob, setChildDob] = useState('')
  const [childDue, setChildDue] = useState('')
  const [packs, setPacks] = useState<PackKey[]>(['early_care'])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const packChoices: PackKey[] = ['early_care', 'shopping', 'health', 'school', 'sports', 'activity', 'pregnancy', 'safety', 'needs']

  function togglePack(p: PackKey) {
    setPacks((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      // Match the deployed families edge function contract:
      //   { display_name, owner_display_name, baby?: { display_name, due_date?, birthdate? } }
      const payload: Record<string, unknown> = {
        display_name: familyName.trim() || `${ownerName.trim()}'s Family`,
        owner_display_name: ownerName.trim() || user?.email || 'Owner',
      }
      if (childType !== 'none') {
        // Force a real date — no placeholder-today, since a wrong DOB / due_date
        // cascades into every age calculation and life-stage transition downstream.
        if (childType === 'baby' && !childDob) {
          throw new Error('Please enter the date of birth')
        }
        if (childType === 'unborn' && !childDue) {
          throw new Error('Please enter the due date')
        }
        const babyBody: Record<string, unknown> = {
          display_name: childName.trim() || (childType === 'unborn' ? 'Little One' : 'Baby'),
        }
        if (childType === 'baby') babyBody.birthdate = childDob
        if (childType === 'unborn') babyBody.due_date = childDue
        payload.baby = babyBody
      }
      const { data, error } = await supabase.functions.invoke('families', { body: payload })
      if (error) throw new Error(error.message)
      if (!data || typeof data !== 'object' || !('family' in data)) {
        throw new Error(`Family creation returned unexpected shape: ${JSON.stringify(data)}`)
      }

      // Persist selected packs onto the newly-created family row.
      // (The edge function does not accept active_packs; we set it via direct table update
      //  which RLS allows because the caller is now the family owner.)
      const familyId = (data as { family: { id: string } }).family.id
      if (packs.length > 0) {
        await supabase.from('families').update({ active_packs: packs }).eq('id', familyId)
      }

      // Lifecycle signal — first family created. Populates Dan's oakden-hub dashboard.
      import('@/lib/errorReporter').then((m) => m.reportInfo('family_bootstrapped', {
        family_id: familyId,
        has_baby: childType !== 'none',
        packs_active: packs.length,
      }))

      await refresh()
      // First-run: the smart wizard fills the parent's week with recurring reminders
      // instead of dumping them on an empty Today screen. Skip wizard if the family
      // was created without a person (Just me for now) — no age context to seed from.
      nav(childType === 'none' ? '/' : '/onboard', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Screen title="Start a family" back>
      <form onSubmit={submit} className="px-5 py-5 space-y-6">
        <div>
          <Kicker>Your name</Kicker>
          <div className="mt-2">
            <Field label="Display name">
              <TextInput
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder="What your kids call you"
                autoFocus
                required
              />
            </Field>
          </div>
        </div>

        <div>
          <Kicker>Family name</Kicker>
          <div className="mt-2">
            <Field label="Family display name" hint="Shown to other caregivers you invite.">
              <TextInput
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="The Oakden Family"
              />
            </Field>
          </div>
        </div>

        <div>
          <Kicker>First person on the graph</Kicker>
          <div className="mt-2 space-y-3">
            <Field label="Who">
              <Select value={childType} onChange={(e) => setChildType(e.target.value as 'baby' | 'unborn' | 'none')}>
                <option value="baby">Baby (already born)</option>
                <option value="unborn">Unborn (pregnancy)</option>
                <option value="none">Just me for now</option>
              </Select>
            </Field>

            {childType !== 'none' && (
              <>
                <Field label="Their name">
                  <TextInput
                    value={childName}
                    onChange={(e) => setChildName(e.target.value)}
                    placeholder={childType === 'unborn' ? 'Nickname while unborn' : 'First name'}
                  />
                </Field>
                {childType === 'baby' && (
                  <Field label="Date of birth" hint="Powers age-based recommendations">
                    <TextInput type="date" value={childDob} onChange={(e) => setChildDob(e.target.value)} />
                  </Field>
                )}
                {childType === 'unborn' && (
                  <Field label="Due date">
                    <TextInput type="date" value={childDue} onChange={(e) => setChildDue(e.target.value)} />
                  </Field>
                )}
              </>
            )}
          </div>
        </div>

        <div>
          <Kicker>Turn on packs</Kicker>
          <p className="mt-1 text-caption text-text-tertiary">
            Packs are domain surfaces. Start with what you'll use this week. Add more anytime.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {packChoices.map((p) => {
              const selected = packs.includes(p)
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => togglePack(p)}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-caption font-medium transition-colors"
                  style={
                    selected
                      ? { background: PACK_COLORS[p].light, color: '#0B0F0E' }
                      : { background: 'rgba(255,255,255,0.05)', border: `1px solid ${PACK_COLORS[p].light}55`, color: PACK_COLORS[p].light }
                  }
                >
                  {PACK_LABELS[p]}
                </button>
              )
            })}
          </div>
        </div>

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          packColor={PACK_COLORS.early_care.light}
          loading={busy}
          className="w-full"
        >
          Create family
        </Button>
      </form>
    </Screen>
  )
}
